import clsx from "clsx";
import React, { FC } from "react";
import { Box, Button, Card, Chip, Typography, alpha } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  nutritionPairDescription,
  nutritionPairTitle,
  NutritionTargetPairs,
} from "../../../constants";
import { CalendarFold, Dumbbell, CalendarRange } from "lucide-react";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },
  controlsContainer: {},
  description: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      paddingInline: theme.spacing(2),
    },
  },
  cardContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  card: {
    radius: 12,
    cursor: "pointer",
    border: `2px solid ${theme.palette.divider}`,
    transition: " background-color 0.3s, border-color 0.3s",
    boxShadow: "none",
    padding: theme.spacing(3),
  },
  selectedCard: {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),

    "& svg": {
      color: theme.palette.primary.main,
    },
  },
  cardContent: {},
  titleContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "start",
    gap: theme.spacing(1),
  },
  icon: {
    fontSize: 26,
    marginBottom: theme.spacing(1),
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  button: {
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    padding: theme.spacing(1.5),
  },
  cancelButton: {
    flex: 1,
    color: theme.palette.common.black,
  },
  changeButton: {
    flex: 2,
  },
  chip: {
    borderRadius: 8,
    fontWeight: 600,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
  },
}));

export interface SelectTargetPairProps {
  pair: NutritionTargetPairs;
  onChangePair: (pair: NutritionTargetPairs) => void;
  onCancel: () => void;
  buttonLabel: string;
  currentlyUsedPair?: NutritionTargetPairs;
}

const SelectTargetPair: FC<SelectTargetPairProps> = ({
  pair: initialPair,
  currentlyUsedPair,
  buttonLabel,
  onChangePair,
  onCancel,
}) => {
  const s = useStyles();
  const [pair, setPair] = React.useState<NutritionTargetPairs>(initialPair);

  const pairOptions: {
    [key in NutritionTargetPairs]: {
      title: string;
      description: string;
      icon: React.ReactNode;
    };
  } = {
    [NutritionTargetPairs.ALL]: {
      title: nutritionPairTitle[NutritionTargetPairs.ALL],
      description: nutritionPairDescription[NutritionTargetPairs.ALL],
      icon: <CalendarFold className={s.icon} />,
    },
    [NutritionTargetPairs.TRAINING_REST]: {
      title: nutritionPairTitle[NutritionTargetPairs.TRAINING_REST],
      description: nutritionPairDescription[NutritionTargetPairs.TRAINING_REST],
      icon: <Dumbbell className={s.icon} />,
    },
    [NutritionTargetPairs.WEEKDAYS_WEEKENDS]: {
      title: nutritionPairTitle[NutritionTargetPairs.WEEKDAYS_WEEKENDS],
      description:
        nutritionPairDescription[NutritionTargetPairs.WEEKDAYS_WEEKENDS],
      icon: <CalendarRange className={s.icon} />,
    },
  };

  const handleCardClick = (selectedPair: NutritionTargetPairs) => {
    setPair(selectedPair);
  };

  const handleConfirm = () => {
    onChangePair(pair);
  };

  return (
    <Box className={s.container}>
      <Box className={s.controlsContainer}>
        <Typography
          className={s.description}
          variant="body2"
          color="textSecondary"
        >
          Choose a schedule that works best for your client.
        </Typography>
        <Box className={s.cardContainer}>
          {(Object.keys(pairOptions) as NutritionTargetPairs[]).map(
            (option) => {
              const optionData = pairOptions[option];
              return (
                <Card
                  key={option}
                  className={`${s.card} ${pair === option ? s.selectedCard : ""}`}
                  onClick={() => handleCardClick(option)}
                >
                  <Box className={s.cardContent}>
                    <Box>{optionData.icon}</Box>
                    <Box className={s.titleContainer}>
                      <Typography variant="subtitle1">
                        {optionData.title}
                      </Typography>
                      {currentlyUsedPair && currentlyUsedPair === option && (
                        <Chip label="current" size="small" className={s.chip} />
                      )}
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {optionData.description}
                    </Typography>
                  </Box>
                </Card>
              );
            },
          )}
        </Box>
      </Box>
      <Box className={s.buttonsContainer}>
        <Button
          color="secondary"
          variant="outlined"
          className={clsx(s.button, s.cancelButton)}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className={clsx(s.button, s.changeButton)}
          onClick={handleConfirm}
          disabled={currentlyUsedPair && currentlyUsedPair === pair}
        >
          {buttonLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default SelectTargetPair;
